// @flow
import React, { Component } from 'react'
import _ from 'lodash'
import { translateFunc } from '../shared/localize'
import styled from 'styled-components'
import ReactSelect from 'react-select'
import Modal from 'react-responsive-modal'
import Spinner from './controls/Spinner'
import { OrtSelectComponents, OrtSelectStyles } from './controls/Select'
import postJson, { postUrl } from '../services/postJson'
import getJson from '../services/getJson'
import hash from 'object-hash'

const REQUEST_LIMIT = 300
const createAdPath = '/audiences/new'

const Error = styled.div`
  display: inline-block;
  font-size: 14px;
  color: red;
  padding-top: 5px;
`

const FixAction = styled.a`
  display: inline-block;
  padding: 0 3px;
`

const t = translateFunc({
  choose_cabinet: 'Выберите кабинет',
  ajax_fail_msg: 'Ошибка запроса',
  are_you_sure:
    'Вы уверенны, что хотите выбрать кабинет "%{name}"? После сохранения изменить выбранный кабинет будет возможно только через запрос в техподдержку.',
  google_account_type_personal: 'Персональные аккаунты',
  google_account_type_unknown: 'Неизвестно',
  accounts_are_loading: 'Загружается список аккаунтов',
  accounts_arent_loaded:
    'Не удалось загрузить список аккаунтов. Перезагрузите страницу или обратитесь в техподдержку',
  reset_token: 'Авторизовать другой аккаунт',
  reload_accounts: 'Загрузить список кабинетов повторно',
  account_success_line_1: 'Аккаунт успешно добавлен!',
  account_success_line_2: 'Вы можете добавить аккаунт другой рекламной сети',
  account_success_line_3_goto: 'или перейти к',
  account_success_line_3_ads: 'Созданию рекламы',
  no_elama_accounts: 'Нет доступных аккаунтов для подключения. Вам нужно',
  goto_elama: 'перейти в eLama',
  get_new_accounts: 'и подключить аккаунт нужной рекламной сети',
  no_accounts: 'Не найдено ни одного аккаунта',
  no_results: 'Ничего не найдено'
})

type SelectedAccount = {|
  id: string,
  name: string
|}

type VkClient = {|
  id: string,
  name: string
|}

type VkAccount = {|
  account_name: string,
  account_id: string,
  account_type: string,
  clients?: Array<VkClient>
|}

type TmAccount = {|
  id: string,
  name: string
|}

type FbAccount = {|
  id: string,
  name: string,
  account_id: string,
  business_name: string
|}

type GlAccount = {|
  id: string,
  name: string,
  account_type: string,
  mcc_name: string
|}

type Response = {|
  data: Array<Object>,
  error?: string,
  selected?: string
|}

type SelectOption = {|
  value: string,
  label: string,
  isLeftPad?: boolean,
  isDisabled?: boolean
|}

type RootOption = {|
  value: string,
  label: string,
  options?: Array<SelectOption>
|}

type Props = {|
  getAccountsUrl: string,
  setAccountUrl: string,
  provider: string,
  selectedAccount: ?SelectedAccount,
  error: ?string,
  resetTokenUrl: string,
  reloadAccountsUrl: string,
  isLoading: boolean,
  loadedAccounts?: Array<Object>
|}

type State = {|
  selectedAccount: ?SelectedAccount,
  error: ?string,
  isLoading: boolean,
  requestLimit: number,
  showSuccessModal: boolean,
  vkAccounts: { [string]: VkAccount },
  vkClients: { [string]: VkClient },
  tmAccounts: { [string]: TmAccount },
  fbAccounts: { [string]: FbAccount },
  glAccounts: { [string]: GlAccount }
|}

const ModifiedOrtSelectStyles = _.cloneDeep(OrtSelectStyles)

ModifiedOrtSelectStyles.option = (provided: Object, state: Object) => {
  const extraStyles = state.data.isLeftPad ? { paddingLeft: '20px' } : {}
  return { ...OrtSelectStyles.option(provided, state), ...extraStyles }
}

function arrayToHashObject(collection: Array<Object>): { [string]: Object } {
  return _.fromPairs(_.map(collection, (item) => [hash(item), item]))
}

export default class ConnectionAccountSelect extends Component<Props, State> {
  timerId: IntervalID

  constructor(props: Props) {
    super(props)

    const { selectedAccount, error, isLoading } = props
    const loadedAccounts: Array<Object> = props.loadedAccounts || []

    this.state = {
      ...this.loadedAccountsToState(loadedAccounts, props.provider),
      selectedAccount,
      error,
      isLoading,
      requestLimit: REQUEST_LIMIT,
      showSuccessModal: false
    }
  }

  componentDidMount() {
    this.timerId = setInterval(() => {
      const { isLoading, requestLimit } = this.state
      if (isLoading && requestLimit > 0) {
        this.setState({ requestLimit: requestLimit - 1 })
        getJson(this.props.getAccountsUrl)
          .then((response: Response) => {
            if (response.error) {
              this.setState({ error: response.error, isLoading: false })
            } else if (response.data) {
              this.setState({
                ...this.loadedAccountsToState(response.data, this.props.provider),
                isLoading: false
              })
            } else if (response.selected) {
              this.setState({
                selectedAccount: response.selected,
                isLoading: false,
                showSuccessModal: true,
                error: null
              })
            }
          })
          .fail(() => {
            this.setState({ requestLimit: 0 })
            window.alert(t('ajax_fail_msg'))
          })
      }
    }, 1000)
  }

  loadedAccountsToState(accounts: Array<Object>, provider: string) {
    return {
      vkAccounts: provider === 'vk' ? arrayToHashObject(accounts) : {},
      vkClients:
        provider === 'vk'
          ? arrayToHashObject(_.flatten(_.compact(_.map(accounts, (account) => account.clients))))
          : {},
      tmAccounts: provider === 'target_mail' ? arrayToHashObject(accounts) : {},
      fbAccounts: provider === 'facebook' ? arrayToHashObject(accounts) : {},
      glAccounts: provider === 'google' ? arrayToHashObject(accounts) : {}
    }
  }

  componentWillUnmount() {
    clearInterval(this.timerId)
  }

  optionSelected(selectedOption: SelectOption) {
    const { setAccountUrl } = this.props

    const selectedAccount = this.findSelectedAccount(selectedOption.value)

    if (!window.confirm(t('are_you_sure', { name: selectedAccount.name }))) {
      return
    }

    postJson(setAccountUrl, { selected: selectedAccount.id })
      .then(() => {
        this.setState({
          selectedAccount: { id: selectedAccount.id, name: selectedAccount.name },
          showSuccessModal: true
        })
      })
      .fail(() => {
        window.alert(t('ajax_fail_msg'))
      })
  }

  findSelectedAccount(id: string): SelectedAccount {
    const dummy = { id: 'not found', name: 'not found' }
    switch (this.props.provider) {
      case 'vk':
        const vkAccount = this.state.vkAccounts[id]
        const vkClient = this.state.vkClients[id]
        return (
          (vkAccount && { id: vkAccount.account_id, name: vkAccount.account_name }) ||
          (vkClient && { id: vkClient.id, name: vkClient.name }) ||
          dummy
        )
      case 'target_mail':
        const tmAccount = this.state.tmAccounts[id]
        return (tmAccount && { id: tmAccount.id, name: tmAccount.name }) || dummy
      case 'facebook':
        const fbAccount = this.state.fbAccounts[id]
        return (fbAccount && { id: fbAccount.id, name: fbAccount.name }) || dummy
      case 'google':
        const glAccount = this.state.glAccounts[id]
        return (glAccount && { id: glAccount.id, name: glAccount.name }) || dummy
      default:
        return dummy
    }
  }

  loadedAccountsToOptions(): Array<SelectOption> {
    const { provider } = this.props

    switch (provider) {
      case 'vk':
        return this.rootOptionsToSimpleOptions(this.vkAccountsToOptions(this.state.vkAccounts))
      case 'target_mail':
        return this.tmAccountsToOptions(this.state.tmAccounts)
      case 'facebook':
        return this.rootOptionsToSimpleOptions(this.fbAccountsToOptions(this.state.fbAccounts))
      case 'google':
        return this.rootOptionsToSimpleOptions(this.glAccountsToOptions(this.state.glAccounts))
      default:
        return [{ value: '-', label: `Unknown provider "${provider}"!` }]
    }
  }

  vkAccountsToOptions(accounts: { [string]: VkAccount }): Array<RootOption> {
    const ids = _.keys(accounts)
    return _.flattenDeep(
      _.map(
        _.sortBy(ids, (id) => accounts[id].account_name),
        (id) => {
          const account = accounts[id]
          return account.account_type === 'agency'
            ? [
                {
                  value: id,
                  label: `${account.account_name} (${account.account_id})`,
                  options: _.map(account.clients, (client) => ({
                    value: hash(client),
                    label: `${client.name} (${client.id})`
                  }))
                }
              ]
            : {
                value: id,
                label: `${account.account_name} (${account.account_id})`
              }
        }
      )
    )
  }

  tmAccountsToOptions(accounts: { [string]: TmAccount }): Array<SelectOption> {
    return _.sortBy(
      _.map(accounts, (account) => ({
        value: hash(account),
        label: `${account.name} (${account.id})`
      })),
      (option) => option.label
    )
  }

  fbAccountsToOptions(accounts: { [string]: FbAccount }): Array<RootOption> {
    return _.flatten(
      _.map(
        _.groupBy(accounts, (account: FbAccount) => account.business_name),
        (accountGroup, groupingValue) => {
          return {
            value: groupingValue,
            label: groupingValue,
            options: _.map(accountGroup, (account) => ({
              value: hash(account),
              label: `${account.name} (${account.id})`
            }))
          }
        }
      )
    )
  }

  glAccountsToOptions(accounts: { [string]: GlAccount }): Array<RootOption> {
    return _.flatten(
      _.map(
        _.groupBy(accounts, (account: GlAccount) => {
          switch (account.account_type) {
            case 'mcc':
              return account.mcc_name
            case 'personal':
              return t('google_account_type_personal')
            default:
              return t('google_account_type_unknown')
          }
        }),
        (accountGroup, groupingValue) => {
          return {
            value: groupingValue,
            label: groupingValue,
            options: _.map(accountGroup, (account) => ({
              value: hash(account),
              label: `${account.name} (${account.id})`
            }))
          }
        }
      )
    )
  }

  rootOptionsToSimpleOptions(rootOptions: Array<RootOption>): Array<SelectOption> {
    return _.flattenDeep(
      _.map(
        _.sortBy(rootOptions, (option) => option.label),
        (rootOption: RootOption): any => {
          if (rootOption.options) {
            const options: Array<SelectOption> = _.sortBy(
              rootOption.options,
              (option) => option.label
            )
            return [
              { value: rootOption.value, label: rootOption.label, isDisabled: true },
              _.map(options, (option: SelectOption): SelectOption => {
                return { value: option.value, label: option.label, isLeftPad: true }
              })
            ]
          } else {
            return { value: rootOption.value, label: rootOption.label }
          }
        }
      )
    )
  }

  render() {
    return (
      <div>
        {this.renderModals()}
        {this.renderConnectionLine()}
      </div>
    )
  }

  renderModals() {
    if (!this.state.showSuccessModal) return

    return (
      <Modal
        open={this.state.showSuccessModal}
        onClose={() => {
          this.setState({ showSuccessModal: false })
        }}
      >
        <div>{t('account_success_line_1')}</div>
        <div>{t('account_success_line_2')}</div>
        <div>
          {t('account_success_line_3_goto')}{' '}
          <a href={createAdPath}>{t('account_success_line_3_ads')}</a>
        </div>
      </Modal>
    )
  }

  renderConnectionLine() {
    const { error, isLoading, selectedAccount } = this.state

    if (isLoading) return this.renderLoading()
    if (error) return this.renderError(error)
    if (selectedAccount) return this.renderSelectedAccount(selectedAccount)

    return (
      <ReactSelect
        arrowRenderer={() => null}
        placeholder={t('choose_cabinet')}
        style={{ borderColor: 'red' }}
        options={this.loadedAccountsToOptions()}
        noOptionsMessage={() => t('no_results')}
        styles={ModifiedOrtSelectStyles}
        components={OrtSelectComponents}
        onChange={(option) => this.optionSelected(option)}
      />
    )
  }

  renderSelectedAccount(account: SelectedAccount) {
    return (
      <div>
        <span className="glyphicon glyphicon-ok" /> {account.name} ({account.id})
      </div>
    )
  }

  renderLoading() {
    if (this.state.requestLimit === 0) {
      return <div>{t('accounts_arent_loaded')}</div>
    }
    return (
      <div>
        <Spinner className="spinner" /> {t('accounts_are_loading')}...
      </div>
    )
  }

  renderError(errorText: string) {
    if (!errorText) return

    return (
      <div>
        <Error>{this.renderErrorText(errorText)}</Error>{' '}
        <FixAction href="#" onClick={(e) => this.resetToken(e)}>
          {t('reset_token')}
        </FixAction>{' '}
        <FixAction href="#" onClick={(e) => this.reloadAccounts(e)}>
          {t('reload_accounts')}
        </FixAction>
      </div>
    )
  }

  renderErrorText(text: string) {
    if (text === 'empty_elama') {
      return (
        <span>
          <span>{t('no_elama_accounts')} </span>
          <a target="_blank" href="https://my.elama.ru/?systemGroup=_attach&utm_source=OneRetarget">
            {t('goto_elama')}
          </a>
          <span> {t('get_new_accounts')}</span>
        </span>
      )
    }
    if (text === 'empty_provider') {
      return t('no_accounts')
    }

    return text
  }

  resetToken(e: any) {
    e.preventDefault()
    postUrl(this.props.resetTokenUrl)
      .then(() => {
        window.location.reload()
      })
      .fail(() => {
        window.alert(t('ajax_fail_msg'))
      })
  }

  reloadAccounts(e: any) {
    e.preventDefault()
    postUrl(this.props.reloadAccountsUrl)
      .then(() => {
        this.setState({ isLoading: true, requestLimit: REQUEST_LIMIT })
      })
      .fail((e) => {
        console.error(e)
        window.alert(t('ajax_fail_msg'))
      })
  }
}
